(function () {
  const selectors = {
    sticky: "[data-bf-sticky]",
    firstProduct: "[data-bf-product-first]",
    countdown: "[data-countdown-target]",
    discountButton: "[data-discount-code]",
    addToCartButton: "[data-bf-add-to-cart]",
    bundleButtons: "[data-bf-add-bundle]",
    quantityWrapper: "[data-bf-quantity]",
    faqItems: ".bf-faq",
  };

  const defaultRoutes = {
    cartAddUrl: "/cart/add.js",
    cartJsonUrl: "/cart.js",
    cartPageUrl: "/cart",
    discountBaseUrl: "/discount",
  };

  function getBlackFridayConfig() {
    return window.BlackFridayConfig || {};
  }

  // Rate limiting for cart mutations
  let lastCartMutationTs = 0;
  const CART_MUTATION_GAP = 1000; // 1 second between add-to-cart calls

  async function throttledCartMutation(mutationFn) {
    const now = Date.now();
    const elapsed = now - lastCartMutationTs;
    if (elapsed < CART_MUTATION_GAP) {
      await new Promise((r) => setTimeout(r, CART_MUTATION_GAP - elapsed));
    }
    lastCartMutationTs = Date.now();
    return mutationFn();
  }

  function getRoute(key) {
    const configRoutes = getBlackFridayConfig().routes || {};
    return configRoutes[key] || defaultRoutes[key];
  }

  function buildDiscountRequestUrl(discountCode) {
    const normalizedBase =
      (getRoute("discountBaseUrl") || defaultRoutes.discountBaseUrl).replace(
        /\/$/,
        "",
      );
    const redirectPath = getRoute("cartPageUrl") || defaultRoutes.cartPageUrl;
    const encodedCode = encodeURIComponent(discountCode);
    const encodedRedirect = encodeURIComponent(redirectPath);
    return `${normalizedBase}/${encodedCode}?redirect=${encodedRedirect}`;
  }

  function toggleStickyVisibility(stickyEl, firstProduct) {
    if (!stickyEl) return;
    const hero = document.querySelector(".bf-hero");
    const heroHeight = hero ? hero.offsetHeight : 0;
    let hasUnlockedSticky = !firstProduct;
    const update = () => {
      const shouldShow = hasUnlockedSticky && window.scrollY > heroHeight;
      stickyEl.classList.toggle("is-visible", shouldShow);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });

    if (firstProduct) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const hasScrolledPast = entry.intersectionRatio <= 0;
            if (hasScrolledPast && !hasUnlockedSticky) {
              hasUnlockedSticky = true;
              update();
              observer.disconnect();
            }
          });
        },
        {
          rootMargin: "-300px",
          threshold: [0],
        },
      );

      observer.observe(firstProduct);
    }
  }

  function pad(num) {
    return String(num).padStart(2, "0");
  }

  function initialiseCountdowns() {
    const countdownContainers = document.querySelectorAll(selectors.countdown);
    countdownContainers.forEach((container) => {
      const targetValue = container.getAttribute("data-countdown-target");
      if (!targetValue) return;
      const targetDate = new Date(targetValue);
      if (Number.isNaN(targetDate.getTime())) return;

      const parts = {
        days: container.querySelector('[data-countdown-part="days"]'),
        hours: container.querySelector('[data-countdown-part="hours"]'),
        minutes: container.querySelector('[data-countdown-part="minutes"]'),
        seconds: container.querySelector('[data-countdown-part="seconds"]'),
      };

      const update = () => {
        const now = new Date();
        let diff = targetDate.getTime() - now.getTime();
        if (diff <= 0) {
          diff = 0;
          clearInterval(timer);
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((diff / (1000 * 60)) % 60);
        const seconds = Math.floor((diff / 1000) % 60);

        if (parts.days) parts.days.textContent = pad(days);
        if (parts.hours) parts.hours.textContent = pad(hours);
        if (parts.minutes) parts.minutes.textContent = pad(minutes);
        if (parts.seconds) parts.seconds.textContent = pad(seconds);
      };

      update();
      const timer = setInterval(update, 1000);
    });
  }

  function initialiseDiscountCopy() {
    const buttons = document.querySelectorAll(selectors.discountButton);
    if (!buttons.length) return;

    buttons.forEach((button) => {
      const prefixSpan = button.querySelector("span");
      const originalLabel = prefixSpan ? prefixSpan.textContent : "";
      const successLabel =
        button.getAttribute("data-copy-success") || "Copied!";
      button.setAttribute("aria-live", "polite");

      const reset = () => {
        if (prefixSpan) {
          prefixSpan.textContent = originalLabel;
        }
        button.classList.remove("is-copied");
      };

      button.addEventListener("click", () => {
        const code = button.getAttribute("data-discount-code");
        if (!code) return;

        const applySuccessState = () => {
          if (prefixSpan) {
            prefixSpan.textContent = successLabel;
          }
          button.classList.add("is-copied");
          setTimeout(reset, 2000);
        };

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard
            .writeText(code)
            .then(applySuccessState)
            .catch(() => {
              fallbackCopy(code, applySuccessState);
            });
        } else {
          fallbackCopy(code, applySuccessState);
        }
      });
    });
  }

  function fallbackCopy(text, onSuccess) {
    const tempInput = document.createElement("textarea");
    tempInput.value = text;
    tempInput.setAttribute("readonly", "");
    tempInput.style.position = "absolute";
    tempInput.style.left = "-9999px";
    document.body.appendChild(tempInput);
    tempInput.select();
    try {
      document.execCommand("copy");
      if (typeof onSuccess === "function") onSuccess();
    } catch (err) {
      console.warn("Could not copy discount code", err);
    }
    document.body.removeChild(tempInput);
  }

  async function addVariantToCart(variantId, quantity, retryCount = 0) {
    const formData = new FormData();
    formData.append("items[0][id]", variantId);
    formData.append(
      "items[0][quantity]",
      Number.isNaN(Number(quantity)) ? 1 : Number(quantity),
    );

    const doFetch = () =>
      fetch(getRoute("cartAddUrl"), {
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
        credentials: "include",
        body: formData,
      });

    const response = await throttledCartMutation(doFetch);

    if (response.status === 429 && retryCount < 2) {
      const retryAfter =
        parseInt(response.headers.get("Retry-After"), 10) || 2;
      await new Promise((r) => setTimeout(r, retryAfter * 1000));
      return addVariantToCart(variantId, quantity, retryCount + 1);
    }

    if (!response.ok) {
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const data = await response.json();
        throw new Error(data?.description || "Unable to add to cart");
      }
      throw new Error(`Request failed with status ${response.status}`);
    }

    // Shopify may return JSON with varying content-types; try parsing regardless
    try {
      return await response.json();
    } catch {
      // Response OK but not JSON - cart was updated, return minimal object
      return { ok: true };
    }
  }

  async function addMultipleVariantsToCart(variantIds, retryCount = 0) {
    const formData = new FormData();
    variantIds.forEach((id, index) => {
      formData.append(`items[${index}][id]`, id);
      formData.append(`items[${index}][quantity]`, 1);
    });

    const doFetch = () =>
      fetch(getRoute("cartAddUrl"), {
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
        credentials: "include",
        body: formData,
      });

    const response = await throttledCartMutation(doFetch);

    if (response.status === 429 && retryCount < 2) {
      const retryAfter =
        parseInt(response.headers.get("Retry-After"), 10) || 2;
      await new Promise((r) => setTimeout(r, retryAfter * 1000));
      return addMultipleVariantsToCart(variantIds, retryCount + 1);
    }

    if (!response.ok) {
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const data = await response.json();
        throw new Error(data?.description || "Unable to add to cart");
      }
      throw new Error(`Request failed with status ${response.status}`);
    }

    // Shopify may return JSON with varying content-types; try parsing regardless
    try {
      return await response.json();
    } catch {
      // Response OK but not JSON - cart was updated, return minimal object
      return { ok: true };
    }
  }

  function applyDiscountCode(discountCode) {
    if (!discountCode) return Promise.resolve();
    const discountUrl = buildDiscountRequestUrl(discountCode);
    return fetch(discountUrl, {
      credentials: "same-origin",
    }).catch((error) => {
      console.warn("Discount code could not be applied automatically.", error);
    });
  }

  function openAssortionSideCart() {
    try {
      if (
        window.Assortion &&
        window.Assortion.cart &&
        typeof window.Assortion.cart.open === "function"
      ) {
        window.Assortion.cart.open();
        return;
      }

      if (window.Assortion && typeof window.Assortion.open === "function") {
        window.Assortion.open();
        return;
      }

      const cartTrigger = document.querySelector(
        ".site-header__icon.site-header__cart",
      );
      if (cartTrigger) {
        cartTrigger.click();
        return;
      }

      const cartButton = document.querySelector(
        '[data-cart-toggle], [data-action="toggle-cart"], .header__icon--cart',
      );
      if (cartButton) {
        cartButton.dispatchEvent(new Event("click", { bubbles: true }));
        return;
      }

      console.warn("Assortion side cart could not be opened automatically.");
    } catch (error) {
      console.warn("Failed to open Assortion side cart.", error);
    }
  }

  function initialiseProductAddToCart() {
    const buttons = document.querySelectorAll(selectors.addToCartButton);
    if (!buttons.length) return;

    buttons.forEach((button) => {
      button.addEventListener("click", async (event) => {
        event.preventDefault();
        if (button.disabled) return;

        const variantId = button.getAttribute("data-variant-id");
        if (!variantId) {
          console.warn("Variant ID missing for add-to-cart button.");
          return;
        }

        const discountCode = button.getAttribute("data-discount-code") || "";
        const quantityInputId = button.getAttribute("data-quantity-input-id");
        let quantityFromInput = null;

        if (quantityInputId) {
          const quantityInput = document.getElementById(quantityInputId);
          if (quantityInput) {
            const parsedValue = parseInt(quantityInput.value, 10);
            if (!Number.isNaN(parsedValue) && parsedValue > 0) {
              quantityFromInput = parsedValue;
            } else {
              quantityInput.value = "1";
            }
          }
        }

        const quantity = String(
          quantityFromInput ??
            (parseInt(button.getAttribute("data-quantity"), 10) || 1),
        );
        const originalText = button.textContent;

        button.disabled = true;
        button.classList.add("is-loading");
        button.textContent = window.themeStrings?.addingToCart || "Adding…";

        try {
          await addVariantToCart(variantId, quantity);
          await applyDiscountCode(discountCode.trim());
          if (window.updateBlackFridayCartCount) window.updateBlackFridayCartCount();
          setTimeout(() => {
            openAssortionSideCart();
          }, 500);
        } catch (error) {
          console.warn("Add to cart failed.", error);
        } finally {
          button.disabled = false;
          button.classList.remove("is-loading");
          button.textContent = originalText;
        }
      });
    });
  }

  function initialiseQuantityControls() {
    const quantityBlocks = document.querySelectorAll(selectors.quantityWrapper);
    if (!quantityBlocks.length) return;

    quantityBlocks.forEach((block) => {
      const input = block.querySelector("[data-bf-quantity-input]");
      if (!input) return;

      const decrement = block.querySelector("[data-bf-quantity-decrement]");
      const increment = block.querySelector("[data-bf-quantity-increment]");
      const min = Math.max(1, parseInt(input.getAttribute("min") || "1", 10));
      const step = Math.max(1, parseInt(input.getAttribute("step") || "1", 10));

      const getCurrentValue = () => parseInt(input.value, 10);
      const setValue = (value) => {
        const safeValue = Number.isNaN(value) ? min : Math.max(min, value);
        input.value = safeValue;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      };

      if (decrement) {
        decrement.addEventListener("click", () => {
          setValue(getCurrentValue() - step);
        });
      }

      if (increment) {
        increment.addEventListener("click", () => {
          setValue(getCurrentValue() + step);
        });
      }

      input.addEventListener("change", () => {
        setValue(getCurrentValue());
      });
    });
  }

  function initialiseBundleAddToCart() {
    const buttons = document.querySelectorAll(selectors.bundleButtons);
    if (!buttons.length) return;

    buttons.forEach((button) => {
      button.addEventListener("click", async (event) => {
        event.preventDefault();
        if (button.disabled) return;

        const variantIdsString = button.getAttribute("data-variant-ids");
        if (!variantIdsString) return;

        const variantIds = variantIdsString.split(",").filter(Boolean);
        const discountCode = button.getAttribute("data-discount-code") || "";
        const originalText = button.textContent;

        button.disabled = true;
        button.classList.add("is-loading");
        button.textContent = window.themeStrings?.addingToCart || "Adding…";

        try {
          await addMultipleVariantsToCart(variantIds);
          await applyDiscountCode(discountCode.trim());
          if (window.updateBlackFridayCartCount) window.updateBlackFridayCartCount();
          setTimeout(() => {
            openAssortionSideCart();
          }, 500);
        } catch (error) {
          console.warn("Add bundle failed.", error);
        } finally {
          button.disabled = false;
          button.classList.remove("is-loading");
          button.textContent = originalText;
        }
      });
    });
  }

  function initialiseStickyCart() {
    const cartButton = document.querySelector("[data-bf-sticky-cart]");
    const countBadge = document.querySelector("[data-bf-cart-count]");

    if (!cartButton) return;

    const MIN_REQUEST_GAP = 8000; // ms between fetches to stay under Shopify rate limits
    const ERROR_COOLDOWN_MS = 45000; // pause if Shopify returns 429
    const ACTIVE_POLL_MS = 15000;
    const IDLE_POLL_MS = 60000;

    let lastRequestTs = 0;
    let inFlight = false;
    let queued = false;
    let cooldownUntil = 0;
    let debounceTimer = null;
    let pollTimer = null;

    const updateCount = async () => {
      try {
        const res = await fetch(getRoute("cartJsonUrl"), {
          headers: {
            "X-Requested-With": "XMLHttpRequest",
            Accept: "application/json",
          },
          credentials: "include",
        });
        if (!res.ok) {
          if (res.status === 429) {
            cooldownUntil = Date.now() + ERROR_COOLDOWN_MS;
          }
          throw new Error(`Cart request failed with status ${res.status}`);
        }
        const cart = await res.json();
        if (countBadge) {
          countBadge.textContent = cart.item_count;
          countBadge.classList.toggle("is-hidden", cart.item_count === 0);
        }
      } catch (err) {
        console.warn("Failed to fetch cart count", err);
      } finally {
        lastRequestTs = Date.now();
        inFlight = false;
        if (queued) {
          queued = false;
          scheduleUpdate("queued");
        }
      }
    };

    const scheduleUpdate = (reason = "manual") => {
      const now = Date.now();
      if (now < cooldownUntil) return;
      if (document.hidden && reason === "poll") return;
      if (inFlight) {
        queued = true;
        return;
      }

      const elapsed = now - lastRequestTs;
      if (elapsed < MIN_REQUEST_GAP) {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(
          () => scheduleUpdate(reason),
          MIN_REQUEST_GAP - elapsed,
        );
        return;
      }

      inFlight = true;
      updateCount();
    };

    const startPolling = () => {
      const interval = document.hidden ? IDLE_POLL_MS : ACTIVE_POLL_MS;
      clearTimeout(pollTimer);
      pollTimer = setTimeout(() => {
        scheduleUpdate("poll");
        startPolling();
      }, interval);
    };

    // Initial fetch
    scheduleUpdate("init");

    // Expose update function globally
    window.updateBlackFridayCartCount = () => scheduleUpdate("external");

    // Listen for custom cart update events
    document.addEventListener("cart:updated", () => scheduleUpdate("event"));
    document.addEventListener("cart:refresh", () => scheduleUpdate("event"));

    // Refresh when user returns to the tab
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) {
        scheduleUpdate("visible");
      }
      startPolling();
    });
    window.addEventListener("focus", () => scheduleUpdate("focus"));

    // Poll while respecting rate limits; slower cadence while tab is hidden
    startPolling();

    cartButton.addEventListener("click", (e) => {
      e.preventDefault();
      openAssortionSideCart();
    });
  }

  function initialiseFAQs() {
    const faqItems = document.querySelectorAll(selectors.faqItems);
    if (!faqItems.length) return;

    faqItems.forEach((faq) => {
      faq.addEventListener("click", (e) => {
        // Don't toggle if clicking on the summary itself (let native behavior handle it)
        if (e.target.tagName === "SUMMARY" || e.target.closest("summary")) {
          return;
        }

        // Toggle the details element
        faq.open = !faq.open;
      });
    });
  }

  document.addEventListener("DOMContentLoaded", () => {
    toggleStickyVisibility(
      document.querySelector(selectors.sticky),
      document.querySelector(selectors.firstProduct),
    );
    initialiseCountdowns();
    initialiseDiscountCopy();
    initialiseQuantityControls();
    initialiseProductAddToCart();
    initialiseBundleAddToCart();
    initialiseStickyCart();
    initialiseFAQs();
  });
})();
